import { useState } from 'react';
import toast from 'react-hot-toast';
import usePostGroup from '../../../../API/hooks/groups/usePostGroup';
import useAddVehiclesToGroup from '../../../../API/hooks/groups/useAddVehiclesToGroup';
import useGetVehicles from '../../../../API/hooks/vehicles/useGetVehicles';
import { Vehicle as VehicleType } from '../../../../API/types/Vehicles';
import DropdownFilter from '../../../../components/common/DropdownFilter/DropdownFilter';
import { FilterConfig } from '../../../../components/common/DropdownFilter/types';
import Icon from '../../../../components/common/Icon/Icon';
import { IconKeys } from '../../../../components/common/Icon/IconKeys';
import Tags from '../../../../components/common/Tags';
import { Button } from '../../../../components/ui/Button/Button';

import S from './AddGroupStyles';
import Vehicle from './Vehicle';
import useVehicleThumbnail from '../../../../theme/useVehicleThumbnail';
import Input from '../../../../components/common/Input/Input';
import { Group } from '../../../../API/types/Groups';

type Props = {
  onCancel: () => void;
  fleetId: string;
};

const SEARCH_CONFIG = [
  {
    type: 'sortBy',
    display: 'radio',
    options: [
      // format of key should be [param]_[order<ASC|DESC>]
      { score_DESC: 'Score: most' },
      { score_ASC: 'Score: least' },
    ],
  },
] as FilterConfig[];

function AssignVehicleToGroup({
  onCancel,
  fleetId,
}: Props): JSX.Element {
  const vehicleThumbnail = useVehicleThumbnail();
  const { data, isLoading, isFetching } = useGetVehicles(fleetId, '');
  const [name, setName] = useState('');
  const [selectedVehicles, setSelectedVehicles] = useState<VehicleType[]>([]);
  const [assignError, setAssignError] = useState('');
  const { mutate: addVehiclesToGroup } = useAddVehiclesToGroup(
    () => {},
    (error) => toast.error(`Failed to add vehicles: ${error}`),
  );

  const onSubmitSuccess = (group: Group): void => {
    const vehicleIds = selectedVehicles.map((vehicle) => vehicle._id as string);
    addVehiclesToGroup({ fleetId, groupId: group?._id, vehicleIds });
    setSelectedVehicles([]);
    setAssignError('');
    onCancel();
  };
  const onSubmitFail = (error: string): void => {
    setAssignError(error);
  };

  const { mutate: createGroup } = usePostGroup(onSubmitSuccess, onSubmitFail);

  const handleVehicleAssignment = (): void => createGroup({ fleetId, name });

  const handleVehicleSelection = (vehicle: VehicleType): void => {
    setSelectedVehicles((prev) => {
      const isSelected = prev.some((v) => v._id === vehicle._id);
      if (isSelected) {
        return prev.filter((v) => v._id !== vehicle._id);
      }
      return [...prev, vehicle];
    });
  };

  return (
    <S.Wrapper>
      <div className="titles">
        <Input
          label="Name"
          name="name"
          placeholder="Type group name"
          onChange={(e: any) => setName(e.target.value)}
          value={name}
        />
        <div className="action-bar">
          <div className="bar">
            <div className="assignment-container">
              <Icon iconKey={IconKeys.ARROW_RIGHT} />
              <div className="vehicle-overview">
                {selectedVehicles.length > 0
                  ? (
                    <div className="selected-vehicles">
                      {selectedVehicles.slice(0, 2).map((vehicle) => (
                        <Tags.Vehicle
                          key={vehicle._id}
                          imageSrc={vehicleThumbnail}
                          licensePlateNumber={vehicle.licensePlateNumber}
                          model={
                            `${vehicle.manufacturer ? (`${vehicle.manufacturer} `) : ''}
                            ${vehicle.model ? (`${vehicle.model} `) : ''}`
                          }
                        />
                      ))}
                      {selectedVehicles.length > 2 && (
                        <div className="more-vehicles">+{selectedVehicles.length - 2} more</div>
                      )}
                    </div>
                  )
                  : <div className="vehicle-placeholder">Choose vehicles from below </div>}
              </div>
            </div>
            <div className="buttons-container">
              <Button
                className="cancel-btn"
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button
                className="confirm-btn"
                disabled={selectedVehicles.length === 0 || !name}
                onClick={handleVehicleAssignment}
              >
                Add group
              </Button>
            </div>
          </div>
          {!!assignError && <div className="error"> {assignError} </div>}
        </div>
        <div className="vehicles-toolbar">
          {
            SEARCH_CONFIG.map((filterConfig: FilterConfig) => (
              <DropdownFilter
                key={filterConfig.type}
                config={filterConfig}
                filters={{}}
                setFilters={() => {}}
              />
            ))
          }
        </div>
      </div>
      <div className="vehicles-list">
        {isLoading && <div> Loading... </div>}
        {!isLoading && data?.results && (
          <ul className="list-container">
              {!!data.results.length
                && data.results.map((item: VehicleType) => (
                  <Vehicle
                    key={item?._id as string}
                    vehicle={item}
                    isChecked={selectedVehicles.some((v) => v._id === item._id)}
                    selectVehicle={handleVehicleSelection}
                  />
                ))}
              {!isFetching && !data.results.length && <div className="no-data">No vehicles found</div>}
          </ul>
        )}
      </div>
    </S.Wrapper>
  );
}

export default AssignVehicleToGroup;
